import { useEffect, useMemo, useState } from "react";
import {
  deleteAlumno, listAlumnos, listApoderados, listCursos, saveAlumno,
} from "../../lib/data";
import { formatRut, isValidRut, normalizeRut, formatCLP } from "../../lib/format";
import type { Alumno, Apoderado, Curso, Practica, Regimen } from "../../lib/types";
import { EmptyState, Modal, Spinner } from "../../components/ui";

const BLANK: Alumno = {
  id: "", nombre: "", rut: "", apoderadoId: "", practicas: "volleyball",
  regimen: "normal", valorEspecial: null, fechaIngreso: "", estado: "activo", creadoEn: "",
};

const PRACTICA_LABEL: Record<Practica, string> = { volleyball: "Vóleibol", futbol: "Fútbol", ambas: "Ambas" };
const REGIMEN_LABEL: Record<Regimen, string> = { normal: "Normal", beca: "Beca", exento: "Exento" };

export function Alumnos() {
  const [items, setItems] = useState<Alumno[]>([]);
  const [apoderados, setApoderados] = useState<Apoderado[]>([]);
  const [cursos, setCursos] = useState<Curso[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Alumno>(BLANK);
  const [q, setQ] = useState("");
  const [error, setError] = useState("");

  async function reload() {
    setLoading(true);
    try {
      const [a, apo, cur] = await Promise.all([listAlumnos(), listApoderados(), listCursos()]);
      setItems(a); setApoderados(apo); setCursos(cur);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { reload(); }, []);

  const filtered = useMemo(
    () => items.filter((a) => a.nombre.toLowerCase().includes(q.toLowerCase()) || a.rut.includes(q)),
    [items, q],
  );

  function nuevo() { setDraft({ ...BLANK, fechaIngreso: new Date().toISOString().slice(0, 10) }); setError(""); setOpen(true); }
  function editar(a: Alumno) { setDraft(a); setError(""); setOpen(true); }

  async function guardar() {
    if (!draft.nombre.trim()) return setError("Nombre obligatorio");
    if (!isValidRut(draft.rut)) return setError("RUT del alumno inválido");
    if (!draft.apoderadoId) return setError("Selecciona un apoderado");
    const apo = apoderados.find((x) => x.id === draft.apoderadoId);
    const curso = cursos.find((x) => x.id === draft.cursoId);
    try {
      await saveAlumno({
        ...draft,
        rut: normalizeRut(draft.rut),
        apoderadoNombre: apo?.nombre,
        cursoNombre: curso?.nombre,
        valorEspecial: draft.valorEspecial || null,
      });
      setOpen(false); reload();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function eliminar(a: Alumno) {
    if (!confirm(`¿Eliminar a ${a.nombre}?`)) return;
    await deleteAlumno(a.id); reload();
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-800">Alumnos</h1>
          <p className="text-sm text-slate-500">{items.length} inscritos</p>
        </div>
        <div className="flex gap-2">
          <input className="input max-w-xs" placeholder="Buscar por nombre o RUT…" value={q} onChange={(e) => setQ(e.target.value)} />
          <button className="btn-primary" onClick={nuevo}>+ Nuevo</button>
        </div>
      </div>

      {loading ? (
        <Spinner />
      ) : filtered.length === 0 ? (
        <EmptyState title="Sin alumnos" hint="Agrega el primero. Necesitas al menos un apoderado creado." />
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-slate-100">
              <tr>
                <th className="th">Alumno</th><th className="th">RUT</th><th className="th">Apoderado</th>
                <th className="th">Práctica</th><th className="th">Régimen</th><th className="th">Estado</th><th className="th"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filtered.map((a) => (
                <tr key={a.id} className="hover:bg-slate-50">
                  <td className="td font-medium">{a.nombre}</td>
                  <td className="td">{formatRut(a.rut)}</td>
                  <td className="td">{a.apoderadoNombre || "—"}</td>
                  <td className="td">{PRACTICA_LABEL[a.practicas]}</td>
                  <td className="td">{REGIMEN_LABEL[a.regimen]}</td>
                  <td className="td">
                    <span className={`badge ${a.estado === "activo" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{a.estado}</span>
                  </td>
                  <td className="td text-right">
                    <button className="btn-ghost mr-1 !px-2 !py-1" onClick={() => editar(a)}>Editar</button>
                    <button className="btn-danger !px-2 !py-1" onClick={() => eliminar(a)}>Eliminar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={draft.id ? "Editar alumno" : "Nuevo alumno"}>
        <div className="space-y-3">
          <div>
            <label className="label">Nombre del alumno</label>
            <input className="input" value={draft.nombre} onChange={(e) => setDraft({ ...draft, nombre: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">RUT del alumno</label>
              <input className="input" value={draft.rut} placeholder="12.345.678-9"
                onChange={(e) => setDraft({ ...draft, rut: formatRut(e.target.value) })} />
            </div>
            <div>
              <label className="label">Fecha de ingreso</label>
              <input className="input" type="date" value={draft.fechaIngreso}
                onChange={(e) => setDraft({ ...draft, fechaIngreso: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="label">Apoderado</label>
            <select className="input" value={draft.apoderadoId} onChange={(e) => setDraft({ ...draft, apoderadoId: e.target.value })}>
              <option value="">— Selecciona —</option>
              {apoderados.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Curso</label>
              <select className="input" value={draft.cursoId || ""} onChange={(e) => setDraft({ ...draft, cursoId: e.target.value || undefined })}>
                <option value="">— Sin curso —</option>
                {cursos.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Práctica</label>
              <select className="input" value={draft.practicas} onChange={(e) => setDraft({ ...draft, practicas: e.target.value as Practica })}>
                <option value="volleyball">Vóleibol</option>
                <option value="futbol">Fútbol</option>
                <option value="ambas">Ambas</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Régimen</label>
              <select className="input" value={draft.regimen} onChange={(e) => setDraft({ ...draft, regimen: e.target.value as Regimen })}>
                <option value="normal">Normal</option>
                <option value="beca">Beca (50%)</option>
                <option value="exento">Exento (0)</option>
              </select>
            </div>
            <div>
              <label className="label">Valor especial (opcional)</label>
              <input className="input" type="number" value={draft.valorEspecial ?? ""} placeholder="Sobrescribe la cuota"
                onChange={(e) => setDraft({ ...draft, valorEspecial: e.target.value ? Number(e.target.value) : null })} />
              {draft.valorEspecial ? <p className="mt-1 text-xs text-slate-400">{formatCLP(draft.valorEspecial)}</p> : null}
            </div>
          </div>
          <div>
            <label className="label">Estado</label>
            <select className="input" value={draft.estado} onChange={(e) => setDraft({ ...draft, estado: e.target.value as Alumno["estado"] })}>
              <option value="activo">Activo</option>
              <option value="inactivo">Inactivo</option>
            </select>
          </div>
          {error && <p className="text-sm text-rose-600">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button className="btn-ghost" onClick={() => setOpen(false)}>Cancelar</button>
            <button className="btn-primary" onClick={guardar}>Guardar</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
